import React, { useState } from 'react';

import { Typography } from '@mui/material';
import { AlineButton, AlineTooltip, AnimatedAlineDialog } from 'src/components/generic';
import { PersonIcon } from 'src/components/icons/PersonIcon';
import { RoleTextInput } from 'src/components/RoleTextInput';
import type { WorkflowCollaborator } from 'src/components/workflow';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

export const useCollabDialogStyles = makeStyles()(theme => ({
  formSubmitButtons: {
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
    width: '100%',
  },
  nav: {
    alignItems: 'center',
    display: 'flex',
    height: 48,
    marginBottom: theme.spacing(2),
  },
  paper: {
    width: theme.dialogStyles.standardDialogWidth,
  },
  rightNav: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    display: 'flex',
    flexGrow: 1,
    height: '100%',
    justifyContent: 'flex-end',
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  userContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface ChangeCollaboratorRoleDialogProps {
  collaborator: Pick<WorkflowCollaborator, 'account' | 'id' | 'roleName' | '__typename'>;
  onClose: () => void;
  open: boolean;
}

export function ChangeCollaboratorRoleDialog({
  collaborator,
  onClose,
  open,
}: ChangeCollaboratorRoleDialogProps): JSX.Element {
  const { classes } = useCollabDialogStyles();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [roleName, setRoleName] = useState<string>(collaborator.roleName ?? '');
  const [syncedRoleName, setSyncedRoleName] = useState(collaborator.roleName);
  const [changeCollaboratorAssignment] = Apollo.useChangeCollaboratorAssignmentMutation();
  const dispatchSaveFailed = useDispatchNotification({
    message: 'Could not save the role. Nothing was changed.',
    variant: 'error',
  });
  const assignedRoleName = roleName.trim() || null;
  const dirty = assignedRoleName !== collaborator.roleName;
  /**
   * A placeholder is a collaborator whose label is the only thing naming it, so clearing the label
   * would leave it naming nobody, which the server rejects. The card opens this dialog for every
   * non-org collaborator, placeholders included, so say why Save is off instead of letting the
   * mutation come back with an error.
   */
  const noAssigneeReason =
    !assignedRoleName && !collaborator.account ? 'A placeholder needs a role name' : '';
  const disabledReason = !dirty ? 'Change role to edit' : noAssigneeReason;

  if (syncedRoleName !== collaborator.roleName) {
    setSyncedRoleName(collaborator.roleName);
    setRoleName(collaborator.roleName ?? '');
  }

  /**
   * The card keeps this dialog mounted and only toggles `open`, so state outlives a close. Put the
   * field back to the saved label on the way out, or a discarded edit is still sitting there, and
   * still counted as dirty, the next time it opens.
   */
  function handleClose(): void {
    setRoleName(collaborator.roleName ?? '');
    setIsSubmitting(false);
    onClose();
  }

  /**
   * A rejection has to be caught. Without it the dialog keeps a spinning Save button and never
   * closes, and Cancel is disabled while submitting, so the only way out would be Esc. A label the
   * server rejects, or a placeholder removed in another tab, is a plausible failure rather than
   * a bug.
   */
  async function handleSave(): Promise<void> {
    setIsSubmitting(true);
    try {
      await changeCollaboratorAssignment({
        variables: { input: { assignedRoleName, permissionId: collaborator.id } },
      });
      onClose();
    } catch {
      dispatchSaveFailed();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AnimatedAlineDialog
      classes={{ paper: classes.paper }}
      open={open}
      title={
        <div className={classes.title}>
          <PersonIcon />
          <Typography variant="h1">Edit Role</Typography>
        </div>
      }
      onClose={handleClose}
    >
      <RoleTextInput value={roleName} onChange={setRoleName} />
      <div className={classes.formSubmitButtons}>
        <AlineButton
          color="primary"
          disabled={isSubmitting}
          variant="outlined"
          onClick={handleClose}
        >
          Cancel
        </AlineButton>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AlineButton
              color="primary"
              disabled={Boolean(disabledReason)}
              loading={isSubmitting}
              variant="contained"
              onClick={handleSave}
            >
              Save
            </AlineButton>
          </div>
        </AlineTooltip>
      </div>
    </AnimatedAlineDialog>
  );
}
